import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, ArrowLeft, Edit2, Trash2, Loader2, X, Save, ExternalLink, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import api, { adminList, adminCreate, adminUpdate, adminDelete, fetchAuthors } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const PARENT_MAP = {
  colleges: { subEntity: 'college-pages', publicPath: 'colleges', title: 'College' },
  universities: { subEntity: 'university-pages', publicPath: 'universities', title: 'University' },
  courses: { subEntity: 'course-pages', publicPath: 'courses', title: 'Course' },
  exams: { subEntity: 'exam-pages', publicPath: 'exams', title: 'Exam' },
};

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');

const emptySub = () => ({
  __new: true,
  parent_id: '',
  tab_label: '',
  slug: '',
  tab_order: 100,
  content: '',
  author_id: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  og_image: '',
  canonical_url: '',
});

const AdminSubPages = () => {
  const { entity, parentId } = useParams(); // entity = 'exams' etc; parentId is parent's DB id
  const meta = PARENT_MAP[entity];
  const navigate = useNavigate();
  const { toast } = useToast();

  const [parent, setParent] = useState(null);
  const [pages, setPages] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const slugTouched = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const parents = await adminList(entity);
      const p = parents.find((x) => x.id === parentId);
      setParent(p || null);
      const list = await api.get(`/admin/${meta.subEntity}`, { params: { parent_id: parentId } }).then((r) => r.data);
      setPages(list);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (meta) {
      load();
      fetchAuthors().then(setAuthors).catch(() => {});
    }
    // eslint-disable-next-line
  }, [entity, parentId]);

  if (!meta) return <div className="text-slate-600">Unknown entity.</div>;

  const openNew = () => {
    slugTouched.current = false;
    setActiveTab('basic');
    setEditing({ ...emptySub(), parent_id: parentId });
  };
  const openEdit = (p) => { slugTouched.current = true; setActiveTab('basic'); setEditing({ ...p }); };

  const updateField = (key, val) => {
    setEditing((prev) => {
      const next = { ...prev, [key]: val };
      if (key === 'slug') slugTouched.current = true;
      // Auto-generate slug like "parentSlug-tabLabel"
      if (!slugTouched.current && key === 'tab_label' && parent) {
        next.slug = `${parent.slug}-${slugify(val)}`;
      }
      return next;
    });
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.url;
    } catch (e) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
      return null;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.__new;
      payload.tab_order = Number(payload.tab_order) || 100;
      if (editing.__new) {
        await adminCreate(meta.subEntity, payload);
        toast({ title: 'Created' });
      } else {
        await adminUpdate(meta.subEntity, editing.id, payload);
        toast({ title: 'Updated' });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await adminDelete(meta.subEntity, toDelete.id);
      toast({ title: 'Deleted' });
      setToDelete(null);
      load();
    } catch (e) { toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }); }
  };

  return (
    <div>
      <button onClick={() => navigate(`/admin/${entity}`)} className="text-sm text-slate-600 hover:text-teal-700 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to {meta.title}s
      </button>

      {parent && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-5">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Layers className="w-3.5 h-3.5" /> Managing sub-pages of
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {parent.name || parent.title}
          </h1>
          <div className="text-xs text-slate-500 font-mono mt-1">/{meta.publicPath}/{parent.slug}</div>
        </div>
      )}

      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Sub-pages / Tabs</h2>
          <p className="text-slate-500 text-xs">{pages.length} tab{pages.length !== 1 ? 's' : ''} - the "Overview" tab is always the parent page itself.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Sub-page
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-teal-600" /></div>
        ) : pages.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No sub-pages yet. Add one to create a new tab.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700 w-20">Order</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Tab Label</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">URL Slug</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500">{p.tab_order}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{p.tab_label}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{p.slug}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a href={`/${meta.publicPath}/${p.slug}`} target="_blank" rel="noreferrer" title="View page"
                        className="w-8 h-8 grid place-items-center rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setToDelete(p)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editing?.__new ? 'Add Sub-page' : 'Edit Sub-page'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
              <TabsList className="bg-slate-100 self-start">
                <TabsTrigger value="basic" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Basic</TabsTrigger>
                <TabsTrigger value="content" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">Content</TabsTrigger>
                <TabsTrigger value="seo" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white">SEO</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="mt-3 flex-1 overflow-y-auto pr-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Tab Label *</label>
                    <input value={editing.tab_label || ''} onChange={(e) => updateField('tab_label', e.target.value)}
                      placeholder="Admit Card" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      URL Slug
                      {editing.slug && <span className="ml-2 text-[10px] text-slate-400 font-mono">/{meta.publicPath}/{editing.slug}</span>}
                    </label>
                    <input value={editing.slug || ''} onChange={(e) => updateField('slug', e.target.value)}
                      placeholder="jee-main-admit-card" className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Tab Order</label>
                    <input type="number" value={editing.tab_order ?? 100} onChange={(e) => updateField('tab_order', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Author</label>
                    <select value={editing.author_id || ''} onChange={(e) => updateField('author_id', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm bg-white">
                      <option value="">- No author -</option>
                      {authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="content" className="mt-3 flex-1 overflow-y-auto pr-1">
                <RichTextEditor value={editing.content || ''} onChange={(v) => updateField('content', v)} />
              </TabsContent>

              <TabsContent value="seo" className="mt-3 flex-1 overflow-y-auto pr-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Meta Title</label>
                    <input value={editing.meta_title || ''} onChange={(e) => updateField('meta_title', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Meta Description</label>
                    <textarea rows={3} value={editing.meta_description || ''} onChange={(e) => updateField('meta_description', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Meta Keywords</label>
                    <input value={editing.meta_keywords || ''} onChange={(e) => updateField('meta_keywords', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">OG Image URL</label>
                    <input value={editing.og_image || ''} onChange={(e) => updateField('og_image', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Canonical URL</label>
                    <input value={editing.canonical_url || ''} onChange={(e) => updateField('canonical_url', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter className="mt-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this sub-page?</AlertDialogTitle>
            <AlertDialogDescription>The tab will disappear from the parent page. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSubPages;
